import { useLayoutEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { esquemaLogin } from '@copathy/shared';
import { ErrorApi } from '../lib/api';
import { useLogin } from '../lib/auth';
import { Boton } from '../componentes/Boton';
import { Campo } from '../componentes/Campo';
import { Marca } from '../componentes/Marca';
import { PanelPresentacion } from '../componentes/PanelPresentacion';
import { FormularioRecuperar } from '../componentes/FormularioRecuperar';
import { Candado, Correo, Ojo, OjoTachado } from '../componentes/iconos';

/**
 * Pantalla de acceso.
 *
 * Izquierda: panel de marca, solo en escritorio. Derecha: el formulario, sobre
 * blanco y en una columna angosta — la lectura de un formulario mejora cuando
 * la línea es corta.
 *
 * Entrar y recuperar viven en la MISMA pantalla y se deslizan de lado en vez de
 * navegar a otra ruta.
 */
export function Login() {
  const entrar = useLogin();
  const navegar = useNavigate();

  const [correo, setCorreo] = useState('');
  const [contrasena, setContrasena] = useState('');
  const [verClave, setVerClave] = useState(false);
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [errorGeneral, setErrorGeneral] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const [vista, setVista] = useState<'entrar' | 'recuperar'>('entrar');
  const [alto, setAlto] = useState<number | undefined>(undefined);
  const refEntrar = useRef<HTMLDivElement>(null);
  const refRecuperar = useRef<HTMLDivElement>(null);

  /*
    El alto del riel es el MAYOR de los dos paneles, y no el del panel activo.

    Con el alto siguiendo al panel activo, el contenedor cambiaba de altura al
    mismo tiempo que el contenido se movía de lado, y el movimiento se veía
    diagonal. Fijándolo al mayor de los dos, la transición es horizontal pura.
    El costo es unos pocos píxeles de espacio reservado, y se nota mucho menos
    que el desplazamiento en diagonal.
  */
  useLayoutEffect(() => {
    const a = refEntrar.current;
    const b = refRecuperar.current;
    if (!a || !b) return;
    const medir = () => setAlto(Math.max(a.offsetHeight, b.offsetHeight));
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(a);
    ro.observe(b);
    return () => ro.disconnect();
  }, []);

  async function alEnviar(e: FormEvent) {
    e.preventDefault();
    setErrores({});
    setErrorGeneral(null);

    // El MISMO esquema Zod que valida el servidor.
    const r = esquemaLogin.safeParse({ correo, contrasena });
    if (!r.success) {
      const campos: Record<string, string> = {};
      for (const issue of r.error.issues) {
        const clave = String(issue.path[0] ?? '_');
        campos[clave] ??= issue.message;
      }
      setErrores(campos);
      return;
    }

    setEnviando(true);
    try {
      await entrar(r.data.correo, r.data.contrasena);
      navegar('/', { replace: true });
    } catch (err) {
      if (err instanceof ErrorApi) {
        if (err.campos) setErrores(err.campos);
        else setErrorGeneral(err.message);
      } else {
        setErrorGeneral('No pudimos conectar con el servidor. Revisa tu conexión.');
      }
    } finally {
      setEnviando(false);
    }
  }

  /*
    ── El lienzo gris (revision del 2026-08-26) ──
    Esta era la UNICA pantalla con fondo blanco: el resto de la plataforma vive
    sobre `marino-50`, el lienzo sobre el que flotan los paneles. El cliente lo
    noto de inmediato al entrar. Se usa el MISMO token, no un gris nuevo: un
    segundo gris parecido es lo que vuelve una interfaz sucia sin que nadie sepa
    senalar por que.
  */
  return (
    <div className="flex min-h-screen bg-marino-50">
      <PanelPresentacion />

      <main className="flex min-w-0 flex-1 items-center justify-center bg-marino-50 px-6 py-12">
        <div className="w-full max-w-[20.5rem]">
          {/*
            Marca compacta para móvil y tablet, donde el panel no se muestra.
            Centrada y en `lg`: en una pantalla sin el panel de marca, el
            logotipo es lo unico que identifica donde esta uno.
          */}
          <div className="mb-10 flex justify-center lg:hidden">
            <Marca tamano="lg" />
          </div>

          {/*
            Riel de dos paneles. `inert` saca del foco al que está fuera de
            vista: sin eso se puede tabular hasta un formulario invisible.
          */}
          <div
            className="relative -mx-2 -my-1 overflow-hidden transition-[height] duration-300 ease-out motion-reduce:transition-none"
            style={{ height: alto }}
          >
            <div
              className="flex w-[200%] items-start transition-transform duration-300 ease-out motion-reduce:transition-none"
              style={{
                transform: vista === 'entrar' ? 'translateX(0)' : 'translateX(-50%)',
              }}
            >
              <div
                ref={refEntrar}
                className="w-1/2 shrink-0 px-2 py-1"
                inert={vista !== 'entrar'}
              >
                {/*
                  «Bienvenido de nuevo» no le gusto al cliente, y el subtitulo
                  decia lo mismo que ahora dice el titulo. Se juntan en uno.

                  COPATHY en mayusculas: regla de negocio del 2026-08-26 — la
                  marca va asi en todo texto visible.
                */}
                <h1 className="text-[1.75rem] leading-tight font-bold text-marino-800">
                  Ingresa a tu cuenta de COPATHY
                </h1>

                <form onSubmit={alEnviar} noValidate className="mt-7 space-y-4">
                  <Campo
                    etiqueta="Correo electrónico"
                    type="email"
                    autoComplete="username"
                    inputMode="email"
                    autoFocus
                    icono={<Correo size={18} />}
                    value={correo}
                    onChange={(e) => setCorreo(e.target.value)}
                    error={errores.correo}
                  />

                  <Campo
                    etiqueta="Contraseña"
                    type={verClave ? 'text' : 'password'}
                    autoComplete="current-password"
                    icono={<Candado size={18} />}
                    value={contrasena}
                    onChange={(e) => setContrasena(e.target.value)}
                    error={errores.contrasena}
                    derecha={
                      <button
                        type="button"
                        onClick={() => setVerClave((v) => !v)}
                        title={verClave ? 'Ocultar' : 'Mostrar'}
                        aria-label={verClave ? 'Ocultar contraseña' : 'Ver contraseña'}
                        className="rounded-full p-2.5 text-marino-400 transition hover:bg-marino-50 hover:text-marino-700"
                      >
                        {verClave ? <OjoTachado size={18} /> : <Ojo size={18} />}
                      </button>
                    }
                  />

                  <p className="pt-1 text-right text-sm">
                    <button
                      type="button"
                      onClick={() => setVista('recuperar')}
                      className="font-medium text-marino-500 hover:text-naranja-600"
                    >
                      ¿Olvidaste tu contraseña?
                    </button>
                  </p>

                  {errorGeneral && (
                    <p role="alert" className="text-sm font-medium text-red-600">
                      {errorGeneral}
                    </p>
                  )}

                  <Boton type="submit" cargando={enviando} className="w-full">
                    {enviando ? 'Ingresando…' : 'Ingresar'}
                  </Boton>
                </form>

                {/*
                  Sin pie de «crear cuenta», a proposito.

                  El cliente pidio cambiar ese texto a «No tienes cuenta. Crear
                  una cuenta.» Y en la MISMA revision decidio que la web es
                  exclusivamente administrativa: quien se registra es el
                  trabajador, y el trabajador entra por la app. Un enlace de
                  registro aca invitaria a crear una cuenta por el camino que se
                  acaba de cerrar.

                  El texto que pidio se aplico donde de verdad hay registro:
                  `movil/src/pantallas/Login.tsx`. Las cuentas de esta web se
                  crean por invitacion desde Usuarios, no registrandose.
                */}
              </div>

              <div
                ref={refRecuperar}
                className="w-1/2 shrink-0 px-2 py-1"
                inert={vista !== 'recuperar'}
              >
                <FormularioRecuperar alVolver={() => setVista('entrar')} />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
